import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Grid, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import JsCookie from 'js-cookie';
import { compact, isEqual, isNil, omitBy, pick } from 'lodash';
import logger from 'logger';
import { DateTime } from 'luxon';
import { getRequestContext } from 'server/utils';
import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import useAnalytics from 'src/analytics/useAnalytics';
import AllergenFooter from 'src/components/AllergenFooter';
import HowItWorks from 'src/components/HowItWorks';
import Loading from 'src/components/Loading';
import PromoCodeBanner from 'src/components/PromoCodeBanner';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { postProspect } from 'src/services/rails-service';
import {
  DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
  DESKTOP_MENU_NAV_BAR_HEIGHT,
  DISMISS_ACTIVE_SUBSCRIPTION_MODAL_COOKIE,
  GENERIC_ERROR_MESSAGE,
  MOBILE_MENU_NAV_BAR_HEIGHT,
} from 'src/utils/constants';
import { getSubscriptionRecurringBooleanFromConfig } from 'src/utils/getSubscriptionRecurringBooleanFromConfig';
import {
  cleanQueryParams,
  parseAndReturnSystemParams,
} from 'src/utils/queryParams';
import { parse as parseCookie } from 'tough-cookie';

import AboutYou, {
  FormData as AboutYouFormData,
} from '../components/GetStarted/AboutYou';
import Layout from '../components/Layout';
import FunnelNavBarHeader from '../components/NavBarHeader/FunnelNavBarHeader';
import ConfigContext from '../context/config';
import UserContext from '../context/user';
import {
  CREATE_CART,
  UPDATE_CART,
} from '../graphql-operations/cart-order-service/carts.mutation';
import { GET_CART_BY_ID } from '../graphql-operations/cart-order-service/carts.query';
import {
  CartStatus,
  CreateCartMutation,
  CreateCartMutationVariables,
  GetCartByIdQuery,
  GetCartByIdQueryVariables,
  GetSubscriptionUuiDsV2Query,
  GetSubscriptionUuiDsV2QueryVariables,
  UpdateCartMutation,
  UpdateCartMutationVariables,
} from '../ts/@types/generated/graphql/cart-order-service/graphql';
import { MealSize } from '../ts/@types/generated/graphql/catalog-service/graphql';
import { ClientNames } from '../utils/apolloClient';
import {
  CART_ID_COOKIE_KEY,
  CART_ID_QUERY_PARAM_KEY,
  DISCOUNT_CODE_PARAM_KEY,
} from '../utils/constants';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import ActiveSubscriptionInfoModal from '../components/ActiveSubscriptionInfoModal';
import { GET_SUBSCRIPTION_UUIDS_V2 } from '../graphql-operations/cart-order-service/subscriptionsv2/subscriptionsv2.query';
import { containsActiveSubscription } from '../components/ActiveSubscriptionInfoModal/utils';
import { getDiscountCodeServerSide } from 'src/services/rails-service';
import {
  verifyUserDiscountEligibility,
  DiscountEligibilityResult,
} from 'src/services/cart-order/discounts/v3/discounts.client';
import { Noticeable } from '@honeybadger-io/core/build/src/types';

type PageProps = {
  cartId: string | null;
  mealSize: MealSize;
  queryParams: Record<string, string | string[]> | null;
  discountCodePublicDescription: string | null;
  // The discount code to apply - either from URL query param or tenant's standing new customer discount (computed server-side)
  discountCodeToApply: string | null;
};

const GetStarted: NextPage<PageProps> = ({
  cartId,
  mealSize,
  queryParams,
  discountCodePublicDescription,
  discountCodeToApply,
}) => {
  //for getting query params into component, cast to an array if not already one and extract 1st value
  const {
    referralCode: referralCodeQueryParam,
    zipCode: paramsZipCode,
    name: paramsUserName,
    email: paramsUserEmail,
    tenantEligibilityConfigLogicalId: tenantEligibilityConfigLogicalIdParam,
    preselectedFilters,
  } = parseAndReturnSystemParams(queryParams);
  const theme = useTheme();
  const { identify } = useAnalytics();
  const userContext = useNonNullContext(UserContext);
  const user = userContext.user;
  const tenantConfig = useNonNullContext(ConfigContext);
  const { track } = useAnalytics();

  const router = useRouter();

  // Track discount eligibility for logged in customers
  const [discountEligibility, setDiscountEligibility] =
    useState<DiscountEligibilityResult | null>(null);

  // Check discount eligibility when user is logged in
  useEffect(() => {
    const checkEligibility = async () => {
      if (!user || !discountCodeToApply) {
        setDiscountEligibility(null);
        return;
      }

      try {
        const result = await verifyUserDiscountEligibility(discountCodeToApply);
        setDiscountEligibility(result);
      } catch (err) {
        logger.error({
          msg: 'FailedToVerifyDiscountEligibilityOnGetStarted',
          extra: { discountCode: discountCodeToApply },
          noticable: err as Noticeable,
        });
      }
    };

    checkEligibility();
  }, [user, discountCodeToApply]);

  const isEligibleForDiscount =
    !user || discountEligibility?.eligible !== false;

  // Only use the discount code if user is eligible
  const eligibleDiscountCode = isEligibleForDiscount
    ? discountCodeToApply
    : undefined;

  const {
    data: subscriptions,
    loading: loadingSubscriptions,
    error: subscriptionsError,
  } = useQuery<
    GetSubscriptionUuiDsV2Query,
    GetSubscriptionUuiDsV2QueryVariables
  >(GET_SUBSCRIPTION_UUIDS_V2, {
    context: {
      clientName: ClientNames.CART_ORDER_GRAPH,
    },
    skip: !user,
  });

  useEffect(() => {
    if (subscriptionsError) {
      logger.error({
        msg: 'Failed to get subscriptions',
        noticable: subscriptionsError,
        extra: {
          context: { cartUUID: cartId },
        },
      });
    }
  }, [subscriptionsError, cartId]);

  const hasDismissedActiveSubscriptionModal = useMemo(
    () => JsCookie.get(DISMISS_ACTIVE_SUBSCRIPTION_MODAL_COOKIE) === 'true',
    [],
  );
  const hasActiveSubscription = containsActiveSubscription(
    subscriptions?.dashboardSubscriptionsV2,
  );
  const [isActiveSubscriptionModalOpen, setIsActiveSubscriptionModalOpen] =
    useState(false);

  const {
    data: cartData,
    loading: cartIsLoading,
    error: cartError,
  } = useQuery<GetCartByIdQuery, GetCartByIdQueryVariables>(GET_CART_BY_ID, {
    context: {
      clientName: ClientNames.CART_ORDER_GRAPH,
    },
    variables: {
      uuid: cartId,
    },
    skip: !cartId,
  });

  useEffect(() => {
    if (cartData?.cart) {
      const expired = new Date() >= new Date(cartData.cart.orderOn);

      const ordered = [
        CartStatus.PendingProcessing,
        CartStatus.Processing,
        CartStatus.Processed,
        CartStatus.FailedProcessing,
      ].includes(cartData.cart.status);

      if (expired || ordered) {
        JsCookie.remove(CART_ID_COOKIE_KEY);
        window.location.reload();
      }
      // @todo delete the commented out code after 2024-10.
      //
      // Leaving the code in here for now to have a more visible history that this was removed as part
      // of some UX changes around promo codes in the URL. I'm not sure why it was added but the cart
      // will be found in the cookie so the URL doesn't have include it. Doing the replace triggers a
      // client side load and as a result the page flicker leading to some weird UX.
      // router.replace({
      //   query: { [CART_ID_QUERY_PARAM_KEY]: data.cart.uuid },
      // });
    }
  }, [cartData]);

  useEffect(() => {
    if (cartError) {
      logger.error({
        msg: 'Failed to get cart by ID',
        noticable: cartError,
        extra: {
          context: { cartUUID: cartId },
        },
      });
    }
  }, [cartError, cartId]);

  const cart: GetCartByIdQuery['cart'] = cartData?.cart;

  // conditionally show error text / prospect sign up form
  const [errorText, setErrorText] = useState('');
  const [showProspectForm, setShowProspectForm] = useState(false);

  const [
    createCartMutation,
    { loading: createCartLoading, error: createCartError },
  ] = useMutation<CreateCartMutation, CreateCartMutationVariables>(CREATE_CART);
  const createCart: typeof createCartMutation = (options) =>
    createCartMutation({
      ...options,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
    });

  const [updateCartMutation, { loading: updateCartLoading }] = useMutation<
    UpdateCartMutation,
    UpdateCartMutationVariables
  >(UPDATE_CART, {
    onCompleted: (data) => {
      if (
        data.updateCart?.__typename === 'CartError' &&
        data.updateCart.validation?.status &&
        data.updateCart.validation.status?.length > 0
      ) {
        JsCookie.remove(CART_ID_COOKIE_KEY);
        window.location.reload();
      }
    },
  });
  const updateCart: typeof updateCartMutation = (options) =>
    updateCartMutation({
      ...options,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
    });

  const initialFormData: AboutYouFormData = {
    //cart.funnelData always takes precedence over user data, as user may come from menu page, and have already updated the fields
    email:
      cart?.funnelData.email ||
      paramsUserEmail ||
      (user?.email && user.email) ||
      '',
    fullName:
      cart?.funnelData.name ||
      paramsUserName ||
      (user?.firstName &&
        user?.lastName &&
        user.firstName + ' ' + user?.lastName) ||
      '',
    zipCode: cart?.funnelData.zip || '',
  };

  const routeToMenu = async (cartId: string) => {
    return router.push({
      pathname: `/menu`,
      query: {
        ...router.query,
        [CART_ID_QUERY_PARAM_KEY]: cartId,
      },
    });
  };

  /**
   * Helper to submit a segment track even when cart is successfully created / updated.
   *
   * Just saves some code duplication as there are a few different code paths for cart interaction here
   * and we have to extract a number of properties form the cart object.
   */
  const trackCartModification = (
    cart: Pick<NonNullable<GetCartByIdQuery['cart']>, 'uuid' | 'funnelData'>,
  ) => {
    const properties = {
      cartId: cart.uuid,
      email: cart.funnelData.email,
      metro: cart.funnelData.metro?.id,
      prospect_email: cart.funnelData.email,
      recipient_name: cart.funnelData.name,
      source: 'get-started',
      zip: cart.funnelData.zip,
      isAuthed: !!user,
    };
    track('Goals recipient info updated', properties);
  };

  const [partialSubmitHandler, setPartialSubmitHandler] = useState(
    () => () => {},
  );

  /**
   * Handles the submissions of the form. It's capture the "prospect" data and then it will
   * either create a new cart or update the current one with changed information.
   */
  const onSubmit: SubmitHandler<AboutYouFormData> = async ({
    email,
    fullName: name,
    zipCode: zip,
  }) => {
    const submit = async () => {
      //this has been a noisy warning if this call fails, so adding a guard to eliminate this warning for most cases
      if (email && zip && name) {
        email && identify(undefined, () => {}, { name, zipCode: zip }, email);
        postProspect({
          tenant_id: userContext.tenantId,
          name,
          email,
          source: 'platform get-started page',
          zip_code: zip,
        })
          .then((res) => res)
          .catch(async (err) => {
            // error code 1003 just indicates the user already exists so they can't be added as prospect;
            // we shouldn't log error in these cases.
            const errorCode = await err?.response?.error?.code;
            if (errorCode !== 1003) {
              logger.info({
                msg: 'Failed to post prospect',
                noticable: err,
              });
            }
          });
      }

      const funnelDataParams = omitBy(
        {
          referralCode: referralCodeQueryParam,
          discountCode: eligibleDiscountCode,
        },
        isNil,
      );

      const tenantEligibilityConfigLogicalId =
        tenantEligibilityConfigLogicalIdParam ||
        DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID;

      // @todo Ideally, we'd be able to update the existing Cart if the ZIP changes
      //   instead of creating a new one. We'll need BE changes to accomplish this though.
      const shouldCreateNewCart =
        !cart ||
        cart.funnelData.zip !== zip ||
        cart.funnelData.mealSize !== mealSize;
      if (shouldCreateNewCart) {
        const variables: CreateCartMutationVariables = {
          funnelData: {
            email,
            mealSize,
            name,
            zip,
            recurring: getSubscriptionRecurringBooleanFromConfig(
              tenantConfig.subscriptionToggleConfiguration,
            ),
            // This field will raise a TypeScript error the next time GQL types are regenerated via codegen. The field exists in the backend,
            // but it's been flagged as deprecated. The codegen tool ignores deprecated fields. The permanent fix is to assess whether we've
            // migrated away from the field, but a short-term fix is to simply remove the deprecated boolean from your local cart-order-svc
            // before you generate the types.
            params: funnelDataParams,
            filters: {
              tagIds: [preselectedFilters?.tagId].filter(Boolean),
            },
          },
          cartData: {
            recipientName: name,
            tenantEligibilityConfigLogicalId,
            // This field will raise a TypeScript error the next time GQL types are regenerated via codegen. The field exists in the backend,
            // but it's been flagged as deprecated. The codegen tool ignores deprecated fields. The permanent fix is to asess whether we've
            // migrated away from the field, but a short-term fix is to simply remove the deprecated boolean from your local cart-order-svc
            // before you generate the types.
            orderOn: DateTime.now().toFormat('yyyy-MM-dd'),
            ...(queryParams && { queryParams: queryParams }),
          },
        };
        try {
          const { data } = await createCart({ variables });
          // non-null safe in try block.

          const { createCart: createdCart } = data!;
          JsCookie.set(CART_ID_COOKIE_KEY, createdCart.uuid);
          trackCartModification(createdCart);
          return await routeToMenu(createdCart.uuid);
        } catch (err: any) {
          if (err.message === 'Invalid zip') {
            setErrorText("We're not available in your area yet!");
            setShowProspectForm(true);
          } else {
            setErrorText(GENERIC_ERROR_MESSAGE);
            logger.error({
              msg: 'Error creating cart on get started page',
              noticable: err,
            });
          }
        }
      } else {
        // The existing Cart needs to be updated if the email, name, or params change.
        // The email and name are used to prefill the checkout page. The params holds
        // the promo code to be auto-applied to the cart if valid.
        const oldFunnelData = pick(cart.funnelData, [
          'email',
          'name',
          'params',
        ]);
        const newFunnelData = { email, name, params: funnelDataParams };

        const shouldUpdateCart = !isEqual(oldFunnelData, newFunnelData);
        if (shouldUpdateCart) {
          try {
            const { data } = await updateCart({
              variables: {
                cartData: {
                  recipientName: name,
                },
                funnelData: {
                  email,
                  name,
                  params: funnelDataParams,
                },
                uuid: cart.uuid,
              },
            });

            if (data?.updateCart?.__typename === 'Cart') {
              const { updateCart: updatedCart } = data;
              JsCookie.set(CART_ID_COOKIE_KEY, updatedCart.uuid);
              trackCartModification(updatedCart);
              return routeToMenu(updatedCart.uuid);
            }

            // @todo if the `data` isn't an updatedCart then we can end up here and it's a silent failure, maybe?
          } catch (err: any) {
            logger.error({
              msg: 'Error occurred updating cart on get started',
              noticable: err,
            });
            setErrorText(GENERIC_ERROR_MESSAGE);
          }
        } else {
          return routeToMenu(cart.uuid);
        }
      }
    };

    /**
     * This `onSubmit` function is called by the `AboutYou` child component any time the `Submit` button is clicked. If we detect that
     * the user already has an active subscription, we display a modal informing them. That modal allows the user to navigate to
     * their dashboard or proceed with a new cart in the funnel. In the latter scenario, the modal must have access to the form
     * data and the cart-creation function. To pull this off, we store the `submit` function above in state, which is provided to
     * the modal as a prop.
     *
     * This approach is a bit unorthodox, but the most obvious alternative, hoisting the `AboutYou` form data state up multiple levels, which
     * is a much more invasive change and introduces prop-drilling. `useImperativeHandle` is another potentially more elegant solution.
     */
    if (hasActiveSubscription && !hasDismissedActiveSubscriptionModal) {
      // Stash the submit function, which has the form args enclosed, into state so the modal can call it upon the "dismiss" action
      setPartialSubmitHandler(() => submit);
      setIsActiveSubscriptionModalOpen(true);
      return;
    }

    submit();
  };

  useEffect(() => {
    //handles auto-creation of cart when zip code query param is present. cartId cookie was removed as a condtion
    //due to not auto-creating a cart until user checks out with previous cart, which leaves users stuck in get-started who have a zip code query param.
    if (paramsZipCode) {
      (async () => {
        //if authed, query params data takes precedence over user's data, and if neither is present, set as empty string to reupdate in cart on checkout page
        await onSubmit({
          fullName: (paramsUserName || (user && user.name)) ?? '',
          email: (paramsUserEmail || (user && user.email)) ?? '',
          zipCode: paramsZipCode,
        });
      })();
    }
  }, [paramsZipCode, paramsUserEmail, paramsUserName, cartId, user]);

  let errorMessage: string | undefined = undefined;

  if (router.query?.purchasedCart) {
    errorMessage = 'This cart has already been purchased. Please start over.';
  } else if (router.query?.fetchFail) {
    errorMessage = GENERIC_ERROR_MESSAGE;
  }

  /**
   * Determines if there is a promo code that is/will be associated with this cart and applied, if applicable, during checkout.
   *
   * If there is a referral or discount code in the URL that should take precedence. If we don't give precedence to the URL then
   * a consumer trying to use the code in the URL can't swap to the updated code. They could manually change it on checkout but
   * an end user would expect the code from the URL they followed to be used.
   */
  const cartFunnelDataParams = cart?.funnelData.params;
  const referralCode: string | undefined | null =
    referralCodeQueryParam || cartFunnelDataParams?.referralCode;
  const discountCode: string | undefined | null =
    eligibleDiscountCode || cartFunnelDataParams?.discountCode;

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const ctaScrollRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    window.scrollTo({
      top:
        (ctaScrollRef.current?.offsetTop as number) -
        (isSmallScreen
          ? //mobile and desktop nav bar heights are different, so we have to account for that when scrolling to the form.
            MOBILE_MENU_NAV_BAR_HEIGHT
          : DESKTOP_MENU_NAV_BAR_HEIGHT) -
        20,
      behavior: 'smooth',
    });
  };

  return (
    <Fragment>
      <FunnelNavBarHeader
        cartNode={
          <Button
            onClick={scrollToForm}
            variant={isSmallScreen ? 'text' : 'contained'}
            sx={{ fontWeight: 600 }}
          >
            Get started
          </Button>
        }
      />
      <Layout
        // Need to override the default padding because we want the content to be "full width" but also need to slide it down far enough that it's not under the header.
        contentCssOverrides={{
          maxWidth: '1312px',
          padding: '4rem 16px 0 16px',
        }}
        cssOverrides={{ backgroundColor: theme.palette.grey[50] }}
        bottomSection={
          <BottomSection
            theme={theme}
            isSmallScreen={isSmallScreen}
            tenantConfig={tenantConfig}
            scrollToForm={scrollToForm}
          />
        }
        hideHeader
      >
        {cartIsLoading ||
        loadingSubscriptions ||
        (paramsZipCode && !createCartError) ? (
          <Loading />
        ) : (
          <Box
            sx={{
              paddingTop: '1rem',
              [theme.breakpoints.down('md')]: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
              },
            }}
          >
            <PromoCodeBanner
              discountCode={discountCode ? discountCode : undefined}
              referralCode={referralCode ? referralCode : undefined}
              discountCodePublicDescription={
                // Only show description if the description exists and if the user is eligible for the discount
                discountCodePublicDescription && isEligibleForDiscount
                  ? discountCodePublicDescription
                  : undefined
              }
              authenticated={!!user}
            />
            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.common.white,
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  ['.MuiAlert-icon']: { color: theme.palette.common.white },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            {user && (
              <ActiveSubscriptionInfoModal
                secondaryCopy={
                  'Would you like to edit meals for an existing subscription or start a new purchase?'
                }
                email={user.email}
                closeButtonText={'New Purchase'}
                open={isActiveSubscriptionModalOpen}
                onDismiss={partialSubmitHandler}
              />
            )}

            <AboutYou
              ctaScrollRef={ctaScrollRef}
              onSubmit={onSubmit}
              defaultFormData={initialFormData}
              isLoading={createCartLoading || updateCartLoading}
              errorText={errorText}
              setErrorText={(newErrorText) => {
                setErrorText(newErrorText);
              }}
              showProspectForm={showProspectForm}
              isUserInContext={!!user}
              subtitle={tenantConfig.getStartedPage?.form?.subtitle}
              title={tenantConfig.getStartedPage?.form?.title}
              info={tenantConfig.getStartedPage?.form?.info}
            />
          </Box>
        )}
      </Layout>
    </Fragment>
  );
};

const BottomSection = ({
  theme,
  isSmallScreen,
  tenantConfig,
  scrollToForm,
}: {
  theme: Theme;
  isSmallScreen: boolean;
  tenantConfig: ClientSafeTenantConfig;
  scrollToForm: () => void;
}) => {
  /**
   * default to true if it's not present in tenant config, only hide if explicitly set to false.
   */
  const showHowItWorks =
    tenantConfig.getStartedPage?.showHowItWorks ||
    tenantConfig.getStartedPage?.showHowItWorks === undefined;

  const showIngredientsStandards = tenantConfig.showIngredientStandards;

  return (
    <Fragment>
      <Grid
        container
        rowSpacing={0}
        sx={{
          margin: '0 auto',
          maxWidth: '1312px',
          width: '100%',
          padding: isSmallScreen ? '1rem 16px' : '1rem 16px 1.5rem 16px',
          backgroundColor: theme.palette.grey[50],
        }}
      ></Grid>
      {showHowItWorks ? (
        <Grid
          sx={{
            width: '100vw',
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <HowItWorks />
        </Grid>
      ) : null}
      {showIngredientsStandards ? (
        <Grid
          sx={{
            width: '100vw',
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <AllergenFooter />{' '}
        </Grid>
      ) : null}
      {isSmallScreen ? (
        <Box
          sx={{
            width: '100%',
            margin: '2rem 0',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            sx={{ width: '90%' }}
            onClick={scrollToForm}
            variant="contained"
          >
            Get started
          </Button>
        </Box>
      ) : null}
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  req,
  //Even though currently the 'r=' query param can be both referral AND discount codes, they'll be separated
  //in the near future, so separated them out in the code to reflect the change
  query: {
    size,
    [CART_ID_QUERY_PARAM_KEY]: cartIdQueryParam,
    [DISCOUNT_CODE_PARAM_KEY]: discountCodeQueryParam,
    ...rest
  },
  resolvedUrl,
}) => {
  const { authContext, tenant } = await getRequestContext(req);
  const { headers } = req;
  const getStartedRedirectUrl = tenant.config.getStartedRedirectUrl;

  if (getStartedRedirectUrl) {
    // If the tenant has a getStartedRedirectUrl configured, we redirect to the provided URL. We also pass along any query params in the URL so that things like referral codes and discount codes can be included in the redirect.
    const queryString = resolvedUrl.split('?')[1] || '';

    const separator = getStartedRedirectUrl.includes('?') ? '&' : '?';
    const destination = queryString
      ? `${getStartedRedirectUrl}${separator}${queryString}`
      : getStartedRedirectUrl;

    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  // Deep linked discount code takes precedence over standing new customer discount
  const discountCodeToApply =
    discountCodeQueryParam || tenant.config.standingNewCustomerDiscountCode;

  const queryParams = cleanQueryParams({
    ...rest,
    [DISCOUNT_CODE_PARAM_KEY]: discountCodeQueryParam,
  });

  const mealSize =
    Object.values(MealSize).find(
      (s) => s === (size as string)?.toUpperCase(),
    ) || MealSize.Standard;

  const cookieStrings = headers.cookie?.split(';') || compact([headers.cookie]);
  const cookies = compact(cookieStrings.map((cs) => parseCookie(cs)));

  // Query param takes precedence over cookie
  const cartId =
    (cartIdQueryParam as string) ||
    cookies.find((c) => c.key === CART_ID_COOKIE_KEY)?.value ||
    undefined;

  let discountCodePublicDescription = null;
  if (discountCodeToApply) {
    try {
      const discountCode = await getDiscountCodeServerSide({
        tenantId: authContext.tenantId,
        discountCode: (discountCodeToApply as string).trim().toUpperCase(),

        eligibleForEcomm: true,
      });

      discountCodePublicDescription = discountCode.publicDescription || null;
    } catch (error) {
      logger.error({
        msg: 'FailedToFetchDiscountCode',
        extra: {
          context: {
            appliedDiscountCode: discountCodeToApply,
            tenantId: authContext.tenantId,
            error,
          },
        },
      });
    }
  }

  return {
    props: {
      queryParams,
      mealSize,
      cartId: cartId ?? null,
      discountCodePublicDescription,
      discountCodeToApply: (discountCodeToApply as string) ?? null,
    },
  };
};

export default GetStarted;
